import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { logger } from '../../services/logger';
import ThemeToggle from './ThemeToggle';
import { useThemeContext } from '../../context/ThemeContext';
import { SunIcon, MoonIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

interface NavLinkProps {
  to: string;
  label: string;
  active: boolean;
  onClick: () => void;
  className?: string;
}

const NavLink: React.FC<NavLinkProps> = ({ to, label, active, onClick, className = '' }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`px-4 py-2 rounded-md transition-colors ${
        active
          ? 'bg-blue-500 text-white'
          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
      } ${className}`}
    >
      {label}
    </Link>
  );
};

const Navigation: React.FC = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { isDarkMode, toggleMode: toggleTheme } = useThemeContext();
  
  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/analysis', label: 'Analysis' },
    { path: '/steps', label: 'Steps' },
    { path: '/windows', label: 'Windows' },
    { path: '/test', label: 'Test' }
  ];
  
  const handleNavClick = (path: string) => {
    logger.info(`Navigation clicked: ${path}`);
    setIsOpen(false);
  };
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  
  return (
    <nav className="bg-white dark:bg-gray-900 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-blue-600 dark:text-blue-400 font-bold text-xl">PhishPhind</span>
            </div>
            <div className="hidden md:ml-6 md:flex md:space-x-4">
              {navItems.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  label={item.label}
                  active={location.pathname === item.path}
                  onClick={() => handleNavClick(item.path)}
                />
              ))}
            </div>
          </div>
          
          <div className="hidden md:flex md:items-center">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none"
              title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
            >
              {isDarkMode ? (
                <SunIcon className="h-6 w-6" />
              ) : (
                <MoonIcon className="h-6 w-6" />
              )}
            </button>
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none"
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      <div className={`md:hidden ${isOpen ? 'block' : 'hidden'}`}>
        <div className="px-2 pt-2 pb-3 space-y-1">
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              label={item.label}
              active={location.pathname === item.path}
              onClick={() => handleNavClick(item.path)}
              className="block w-full px-3 py-2"
            />
          ))}
          <button
            onClick={toggleTheme}
            className="flex w-full items-center px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none"
          >
            {isDarkMode ? (
              <>
                <SunIcon className="h-5 w-5 mr-2" />
                <span>Light Mode</span>
              </>
            ) : (
              <>
                <MoonIcon className="h-5 w-5 mr-2" />
                <span>Dark Mode</span>
              </>
            )}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navigation; 